import { z } from 'zod';
import { IsoDateInputSchema, IsoDateSchema, SlugInputSchema, SlugSchema, QuoteSchema } from './entities.js';
import {
  PartyLinkCategorySchema,
  PartyLinkConfidenceSchema,
  PartyLinkDirectionSchema,
  PartyLinkTypeSchema,
  alignPartyLinkWithCatalogue,
} from './party-link-types.js';

/**
 * Case Wiki — schéma `case_party_links` (M068).
 *
 * Pattern slug-as-id (cohérent avec `case_contradictions.fact_a_slug`,
 * `link_fact_party.fact_slug`, etc.) : on expose `source_party_slug` /
 * `target_party_slug`, jamais les UUIDs internes. Le repo résout
 * slug↔UUID en interne.
 *
 * Soft-delete via `deleted_at` — un lien soft-deleted peut être réintroduit
 * avec le même `(source, target, link_type, procedure_id)` (l'unicité
 * partielle DB l'autorise).
 */

export const PartyLinkSchema = z.object({
  id: z.string().uuid(),
  source_party_slug: SlugSchema,
  target_party_slug: SlugSchema,
  category: PartyLinkCategorySchema,
  link_type: PartyLinkTypeSchema,
  subtype: z.string().min(1).max(120).nullable().optional(),
  direction: PartyLinkDirectionSchema,
  valid_from: IsoDateSchema.nullable().optional(),
  valid_to: IsoDateSchema.nullable().optional(),
  procedure_id: z.string().uuid().nullable().optional(),
  source_piece_slugs: z.array(SlugSchema).default([]),
  quote: QuoteSchema.nullable().optional(),
  confidence: PartyLinkConfidenceSchema.default('medium'),
  created_source: z.enum(['agent', 'user', 'agent_validated']).default('agent'),
  /** Si non-null : ce lien a été remplacé par un nouveau (édition manuelle). */
  superseded_by: z.string().uuid().nullable().optional(),
  /** Soft-delete. Non-null = lien retiré. */
  deleted_at: z.string().datetime().nullable().optional(),
  deletion_reason: z.string().max(500).nullable().optional(),
  created_by: z.string().uuid().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime().nullable().optional(),
  edited_by: z.string().uuid().nullable().optional(),
  edited_at: z.string().datetime().nullable().optional(),
});
export type PartyLink = z.infer<typeof PartyLinkSchema>;

/**
 * Payload du tool agent `link_party_to_party` (M068).
 *
 * `category` est facultative à l'ENTRÉE (ce que l'agent envoie) et toujours présente à la SORTIE
 * (ce que reçoit le repository) : la transformation la pose d'après le catalogue, et remplace
 * celle que l'agent aurait donnée à tort. Le type inféré est celui de la sortie.
 */
export const LinkPartyToPartyPayloadSchema = z
  .object({
    source: SlugInputSchema,
    target: SlugInputSchema,
    category: PartyLinkCategorySchema.optional(),
    link_type: PartyLinkTypeSchema,
    subtype: z.string().min(1).max(120).optional(),
    direction: PartyLinkDirectionSchema.optional(),
    valid_from: IsoDateInputSchema.optional(),
    valid_to: IsoDateInputSchema.optional(),
    procedure_id: z.string().uuid().optional(),
    source_piece_slugs: z.array(SlugInputSchema).max(10).optional(),
    quote: QuoteSchema.optional(),
    confidence: PartyLinkConfidenceSchema.optional(),
  })
  .transform((payload) => alignPartyLinkWithCatalogue(payload));
export type LinkPartyToPartyPayload = z.infer<typeof LinkPartyToPartyPayloadSchema>;
